import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Clock, MapPin, Calendar, Phone, ArrowLeft, Zap, MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await axios.get(`${API}/bookings/${bookingId}`);
        setBooking(response.data);
      } catch (err) {
        setError("Booking not found");
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sarathi-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-sarathi-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-sarathi-background flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sarathi-text-secondary mb-4">{error || "Booking not found"}</p>
          <Link to="/">
            <Button variant="outline" data-testid="back-home-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sarathi-background py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <Link to="/" className="inline-flex items-center text-sarathi-text-secondary hover:text-sarathi-primary transition-colors text-sm sm:text-base" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4 mr-1.5 sm:mr-2" />
            <span className="hidden sm:inline">Back to Home</span>
            <span className="sm:hidden">Home</span>
          </Link>
          <button
            type="button"
            onClick={() => navigate("/")} className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-[#71717a] hover:text-white transition-colors" data-testid="close-confirmation-btn">
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        <div className="glass-card p-5 sm:p-8">
          {/* Header - Pending State */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-sarathi-primary/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Clock className="w-7 h-7 sm:w-8 sm:h-8 text-sarathi-primary" />
            </div>
            <h1 className="font-outfit text-xl sm:text-3xl font-bold text-white mb-2 leading-tight" data-testid="confirmation-title">
              Booking Request Received!
            </h1>
            <p className="text-sarathi-text-secondary text-sm sm:text-base">
              We're checking availability for your ride.
            </p>
          </div>

          {/* Booking Details Card */}
          <div className="bg-sarathi-surface rounded-2xl p-5 sm:p-6 mb-5 sm:mb-6 border border-white/10">
            {/* Reference — stacks on mobile, side-by-side on desktop */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-4">
              <span className="text-sarathi-text-muted text-xs sm:text-sm uppercase tracking-wide">
                Booking Reference
              </span>
              <span className="font-outfit font-bold text-sarathi-primary text-base sm:text-lg break-all" data-testid="booking-reference">
                {booking.booking_reference}
              </span>
            </div>

            <div className="h-px bg-white/10 mb-5"></div>

            <div className="space-y-4">
              {/* Pickup */}
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-sarathi-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sarathi-text-muted text-xs sm:text-sm">Pickup</p>
                  <p className="text-white text-sm sm:text-base break-words" data-testid="pickup-location">{booking.pickup_location}</p>
                </div>
              </div>

              {/* Drop-off */}
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-sarathi-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sarathi-text-muted text-xs sm:text-sm">Drop-off</p>
                  <p className="text-white text-sm sm:text-base break-words" data-testid="drop-location">{booking.drop_location}</p>
                </div>
              </div>

              {/* Date & Time - Combined */}
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-sarathi-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm sm:text-base" data-testid="booking-date-time">
                    {booking.date} &middot; {booking.time}
                  </p>
                </div>
              </div>

              {/* Contact - Combined */}
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-sarathi-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm sm:text-base break-words" data-testid="booking-contact">
                    {booking.passenger_name}
                    {booking.passenger_phone ? <> &middot; {booking.passenger_phone}</> : null}
                  </p>
                </div>
              </div>

              {/* Fare */}
              <div className="flex items-start gap-3">
                <span className="text-sarathi-primary mt-0.5 shrink-0 text-base font-bold w-5 text-center">₹</span>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm sm:text-base" data-testid="booking-fare">
                    {booking.fare_data ? (
                      <>Fare: {booking.fare_data.displayTotal} <span className="text-sarathi-text-muted text-xs sm:text-sm">(if confirmed)</span></>
                    ) : (
                      <>Fare will be confirmed on WhatsApp</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-sarathi-primary" />
              <p className="text-white font-semibold text-sm">What happens next?</p>
            </div>
            <p className="text-sarathi-text-secondary text-sm mb-5 sm:mb-6 px-2">
              Our team will review your request and confirm your ride on WhatsApp within 30 minutes.
            </p>

            <a
              href="https://wa.me/919187057303"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-support-link"
              className="inline-block w-full sm:w-auto"
            >
              <Button className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20BD5C] text-white h-12 px-6 rounded-xl">
                <MessageCircle className="w-5 h-5 mr-2" />
                Message Us on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
